<script setup lang="ts">
  // Unified orders history, serving BOTH routes with one behavior:
  // - /:rid/orders (OrdersHistoric, content layout, document scroll)
  // - /:rid/pos/history (OrdersManage, full-bleed workspace, back button)
  // Tabs: Todos / Mesas (closed table sessions) / Llevar / Delivery. Shows
  // ALL orders (live + settled) with date/status/price/search filters; order
  // clicks open PosV2OrderModal, session clicks PosV2SessionOrdersModal.
  import { computed, getCurrentInstance, ref, watch } from 'vue';
  import { refDebounced } from '@vueuse/core';
  import { useRoute, useRouter } from 'vue-router/composables';
  import { OrderMode, OrderStatus } from '@/enums/OrderEnums';
  import { IOrder } from '@/interfaces/IOrder';
  import { orderToListItem } from '@/domain/pos/order-formatting';
  import type { IOrderListItem } from '@/domain/pos/pos-types';
  import {
    DEFAULT_HISTORY_DATE_PRESET,
    getPresetRange,
  } from '@/domain/pos/history-date-presets';
  import type { IHistoryDateRange } from '@/domain/pos/history-date-presets';
  import { sessionToListItem } from '@/domain/pos/closed-session-formatting';
  import type { ISessionListItem } from '@/domain/pos/closed-session-formatting';
  import OrdersService from '@/api/services/orders.service';
  import TableSessionsService from '@/api/services/table-sessions.service';
  import { useModal } from '@/composition/use-modal';
  import { usePosV2 } from '@/composition/use-pos-v2';
  import { useRestaurantStore } from '@/stores/restaurant.store';
  import PosV2OrderModal from '@/components/pos-v2/modals/PosV2OrderModal.vue';
  import PosV2SessionOrdersModal from '@/components/pos-v2/modals/PosV2SessionOrdersModal.vue';
  import PosUiEmptyState from '@/components/pos-v2/ui/PosUiEmptyState.vue';
  import PosV2HistoryHeader from '@/components/pos-v2/history/PosV2HistoryHeader.vue';
  import type { THistoryTypeTab } from '@/components/pos-v2/history/PosV2HistoryHeader.vue';
  import PosV2HistoryList from '@/components/pos-v2/history/PosV2HistoryList.vue';
  import PosV2HistorySessionsList from '@/components/pos-v2/history/PosV2HistorySessionsList.vue';

  const PAGE_SIZE = 50;

  const { isPosV2Active } = usePosV2();
  const restaurantStore = useRestaurantStore();
  const modals = useModal();
  const route = useRoute();
  const router = useRouter();
  const ordersService = new OrdersService();

  const proxy = getCurrentInstance()!.proxy;
  function i18nT(key: string): string {
    return String(proxy.$t(key));
  }

  const isPosRoute = computed(() => !!route.meta?.pos);

  const posPath = computed(() => `/${restaurantStore.restaurant.stringId}/pos`);

  // POS-route-only guard: /pos/history only exists while POS V2 is active.
  // MUST NOT run on /orders, or the page self-destructs on non-V2 branches.
  // isPosRoute is a watch SOURCE too: both routes render this component, so
  // an in-app navigation between them reuses the instance (no re-mount).
  watch(
    [isPosV2Active, isPosRoute],
    ([active, pos]) => {
      if (pos && !active) router.replace(posPath.value);
    },
    { immediate: true }
  );

  // WhatsApp-mode branches have no native orders: notice instead of lists.
  const isWhatsAppBranch = computed(
    () => restaurantStore.selectedBranch?.ordersMode === OrderMode.WhatsApp
  );

  //#region Filters

  const typeTab = ref<THistoryTypeTab>('all');
  const search = ref('');
  const debouncedSearch = refDebounced(search, 200);
  const priceMin = ref<number | null>(null);
  const priceMax = ref<number | null>(null);
  const statuses = ref<OrderStatus[]>([]);
  const sessionTableIds = ref<number[]>([]);
  const dateRange = ref<IHistoryDateRange>(
    getPresetRange(DEFAULT_HISTORY_DATE_PRESET)
  );

  const isTablesTab = computed(() => typeTab.value === 'tables');

  // Branch tables for the Mesas filter popover (branch bootstrap keeps
  // restaurantStore.tables loaded and socket-synced on every admin route).
  const tableOptions = computed(() =>
    (restaurantStore.tables ?? [])
      .map((table) => ({ id: table.id, name: table.name }))
      .sort((a, b) =>
        a.name.localeCompare(b.name, undefined, { numeric: true })
      )
  );

  // Table ids are branch-scoped: a stale selection on another branch would
  // silently zero the results with no visible active filter.
  watch(
    () => restaurantStore.selectedBranch?.id,
    () => {
      sessionTableIds.value = [];
    }
  );

  const dateNarrowed = computed(
    () => dateRange.value.preset !== DEFAULT_HISTORY_DATE_PRESET
  );

  // Drives the "no results — clear filters" empty state per tab (price and
  // status don't apply to the sessions request). The type tab is NOT counted:
  // it's a lens clearFilters deliberately keeps, so counting it would offer a
  // no-op "clear filters" button on an empty type tab.
  const hasFilters = computed(() => {
    const shared = debouncedSearch.value.trim() !== '' || dateNarrowed.value;
    if (isTablesTab.value) return shared || sessionTableIds.value.length > 0;
    return (
      shared ||
      priceMin.value != null ||
      priceMax.value != null ||
      statuses.value.length > 0
    );
  });

  function applyPrice(range: { min: number | null; max: number | null }) {
    priceMin.value = range.min;
    priceMax.value = range.max;
  }

  function applyDate(range: IHistoryDateRange) {
    dateRange.value = range;
  }

  function applyStatuses(value: OrderStatus[]) {
    statuses.value = value;
  }

  function applyTables(value: number[]) {
    sessionTableIds.value = value;
  }

  function clearFilters() {
    // The active tab is a lens, not a filter: it stays.
    search.value = '';
    priceMin.value = null;
    priceMax.value = null;
    statuses.value = [];
    sessionTableIds.value = [];
    dateRange.value = getPresetRange(DEFAULT_HISTORY_DATE_PRESET);
  }

  //#endregion

  //#region Server-paginated data (orders + sessions, one visible at a time)

  const orderRows = ref<IOrder[]>([]);
  const sessionRows = ref<ISessionListItem[]>([]);
  // Fetched with page 1 of the current filter and reused across load-mores
  // (the "Mostrando X de Y" hint never re-counts).
  const ordersTotal = ref(0);
  const sessionsTotal = ref(0);
  const ordersPage = ref(1);
  const sessionsPage = ref(1);
  const isLoading = ref(false);
  const isLoadingMore = ref(false);
  const error = ref<string | null>(null);

  // Discards out-of-order responses (debounced search races page loads, and
  // a tab switch invalidates the other tab's in-flight response too).
  let requestGeneration = 0;

  /**
   * Date window FROZEN at each page-1 fetch: an open-ended range resolves
   * `to` to a concrete "now" that every load-more of the same generation
   * reuses. Without it, live orders arriving between fetches shift the
   * OFFSET and duplicate/skip cards. Recomputed on filter/tab/branch change
   * or retry (all of which go through a page-1 fetch).
   */
  interface IFrozenWindow {
    from: Date;
    to: Date;
  }
  let ordersWindow: IFrozenWindow | null = null;
  let sessionsWindow: IFrozenWindow | null = null;

  function resolveWindow(): IFrozenWindow {
    return {
      from: dateRange.value.from,
      to: dateRange.value.to ?? new Date(),
    };
  }

  async function fetchOrdersPage(targetPage: number): Promise<void> {
    const restaurantId = restaurantStore.restaurant?.id;
    const branchId = restaurantStore.selectedBranch?.id;
    if (!restaurantId || !branchId) return;

    const generation = ++requestGeneration;
    const isFirstPage = targetPage === 1;
    if (isFirstPage) ordersWindow = resolveWindow();
    const dateWindow = ordersWindow!;

    if (isFirstPage) isLoading.value = true;
    else isLoadingMore.value = true;
    error.value = null;

    try {
      const res = await ordersService.getAll(
        restaurantId,
        branchId,
        statuses.value.length > 0 ? statuses.value : null,
        null,
        typeTab.value === 'all' || typeTab.value === 'tables'
          ? null
          : [typeTab.value],
        { page: targetPage, itemsPerPage: PAGE_SIZE },
        {
          search: debouncedSearch.value,
          minTotal: priceMin.value ?? undefined,
          maxTotal: priceMax.value ?? undefined,
          dateFrom: dateWindow.from.toISOString(),
          dateTo: dateWindow.to.toISOString(),
          // Load-mores reuse the page-1 total: skip the expensive COUNT(*).
          skipCount: !isFirstPage,
        }
      );

      if (generation !== requestGeneration) return;

      if (isFirstPage) {
        orderRows.value = res.items;
        ordersTotal.value = res.total;
      } else {
        orderRows.value = [...orderRows.value, ...res.items];
      }
      ordersPage.value = targetPage;
    } catch (e) {
      if (generation !== requestGeneration) return;
      console.error('[pos-v2-history] orders load failed', e);
      error.value = i18nT('posV2.errNoConnection');
    } finally {
      if (generation === requestGeneration) {
        isLoading.value = false;
        isLoadingMore.value = false;
      }
    }
  }

  async function fetchSessionsPage(targetPage: number): Promise<void> {
    const restaurantId = restaurantStore.restaurant?.id;
    const branchId = restaurantStore.selectedBranch?.id;
    if (!restaurantId || !branchId) return;

    const generation = ++requestGeneration;
    const isFirstPage = targetPage === 1;
    if (isFirstPage) sessionsWindow = resolveWindow();
    const dateWindow = sessionsWindow!;

    if (isFirstPage) isLoading.value = true;
    else isLoadingMore.value = true;
    error.value = null;

    try {
      const res = await TableSessionsService.getClosedSessions(
        restaurantId,
        branchId,
        {
          dateFrom: dateWindow.from.toISOString(),
          dateTo: dateWindow.to.toISOString(),
          search: debouncedSearch.value,
          ...(sessionTableIds.value.length > 0
            ? { tableIds: sessionTableIds.value }
            : {}),
          page: targetPage,
          itemsPerPage: PAGE_SIZE,
        }
      );

      if (generation !== requestGeneration) return;

      const items = res.items.map(sessionToListItem);
      if (isFirstPage) {
        sessionRows.value = items;
        sessionsTotal.value = res.total;
      } else {
        sessionRows.value = [...sessionRows.value, ...items];
      }
      sessionsPage.value = targetPage;
    } catch (e) {
      if (generation !== requestGeneration) return;
      console.error('[pos-v2-history] sessions load failed', e);
      error.value = i18nT('posV2.errNoConnection');
    } finally {
      if (generation === requestGeneration) {
        isLoading.value = false;
        isLoadingMore.value = false;
      }
    }
  }

  function fetchFirstPage(): void {
    if (isWhatsAppBranch.value) return;
    if (isTablesTab.value) void fetchSessionsPage(1);
    else void fetchOrdersPage(1);
  }

  const activeTotal = computed(() =>
    isTablesTab.value ? sessionsTotal.value : ordersTotal.value
  );

  const hasMoreOrders = computed(
    () => orderRows.value.length < ordersTotal.value
  );
  const hasMoreSessions = computed(
    () => sessionRows.value.length < sessionsTotal.value
  );

  function loadMore() {
    if (isLoading.value || isLoadingMore.value) return;
    if (isTablesTab.value) void fetchSessionsPage(sessionsPage.value + 1);
    else void fetchOrdersPage(ordersPage.value + 1);
  }

  // Filter/tab/branch changes reset to page 1 of the active tab.
  watch(
    [
      debouncedSearch,
      typeTab,
      statuses,
      sessionTableIds,
      dateRange,
      priceMin,
      priceMax,
      () => restaurantStore.selectedBranch?.id,
    ],
    () => {
      fetchFirstPage();
    },
    { immediate: true }
  );

  const orderListItems = computed<IOrderListItem[]>(() =>
    orderRows.value.map(orderToListItem)
  );

  //#endregion

  function handleOrderClick(item: IOrderListItem) {
    modals.showDynamicDialog(
      PosV2OrderModal,
      { orderId: item.id },
      'pos-v2-order-modal',
      { width: 1100 }
    );
  }

  function handleSessionClick(item: ISessionListItem) {
    modals.showDynamicDialog(
      PosV2SessionOrdersModal,
      { tableSessionId: item.id },
      'pos-v2-session-orders',
      { width: 900 }
    );
  }

  function goBack() {
    router.push(posPath.value);
  }
</script>

<template>
  <div
    class="pos-v2-history-page"
    :class="isPosRoute ? 'variant-workspace' : 'variant-content'"
  >
    <div class="page-frame">
      <PosV2HistoryHeader
        :type-tab.sync="typeTab"
        :search.sync="search"
        :date-range="dateRange"
        :statuses="statuses"
        :table-ids="sessionTableIds"
        :table-options="tableOptions"
        :price-min="priceMin"
        :price-max="priceMax"
        :total="activeTotal"
        :show-back="isPosRoute"
        class="page-header"
        @apply-date="applyDate"
        @apply-statuses="applyStatuses"
        @apply-tables="applyTables"
        @apply-price="applyPrice"
        @back="goBack"
      />

      <PosUiEmptyState
        v-if="isWhatsAppBranch"
        icon="whatsapp"
        :title="i18nT('posV2.historyWaNotice')"
        class="wa-notice"
      />

      <PosV2HistorySessionsList
        v-else-if="isTablesTab"
        :items="sessionRows"
        :total="sessionsTotal"
        :is-loading="isLoading"
        :is-loading-more="isLoadingMore"
        :error="error"
        :has-more="hasMoreSessions"
        :has-filters="hasFilters"
        :variant="isPosRoute ? 'workspace' : 'content'"
        class="page-list"
        @session-click="handleSessionClick"
        @load-more="loadMore"
        @retry="fetchSessionsPage(1)"
        @clear-filters="clearFilters"
      />

      <PosV2HistoryList
        v-else
        :items="orderListItems"
        :total="ordersTotal"
        :is-loading="isLoading"
        :is-loading-more="isLoadingMore"
        :error="error"
        :has-more="hasMoreOrders"
        :has-filters="hasFilters"
        :variant="isPosRoute ? 'workspace' : 'content'"
        class="page-list"
        @order-click="handleOrderClick"
        @load-more="loadMore"
        @retry="fetchOrdersPage(1)"
        @clear-filters="clearFilters"
      />
    </div>
  </div>
</template>

<style scoped lang="scss">
  .pos-v2-history-page {
    // Full-bleed POS workspace: fixed height, lists scroll internally.
    &.variant-workspace {
      @apply tw-flex tw-h-full tw-min-h-0 tw-w-full tw-flex-col tw-bg-canvas;

      .page-frame {
        @apply tw-flex tw-min-h-0 tw-flex-1 tw-flex-col;
      }

      .page-list,
      .wa-notice {
        @apply tw-min-h-0 tw-flex-1;
      }
    }

    // Back-office content archetype: centered max-width card, the DOCUMENT
    // scrolls (the admin shell has no fixed height here — don't fight it).
    &.variant-content {
      @apply tw-block tw-w-full tw-px-4 tw-py-4 md:tw-px-6;

      .page-frame {
        @apply tw-mx-auto tw-max-w-screen-2xl tw-overflow-hidden tw-rounded-xl tw-border tw-border-neutral-200 tw-bg-white;
      }
    }

    .page-header {
      @apply tw-flex-none;
    }
  }
</style>
